import React, { PropsWithChildren, useLayoutEffect, useRef } from 'react';

import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useAuthContext } from 'state/auth';
import { ModalAuthTrigger } from 'state/auth/types';

const AuthRequired = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, isReAuthenticating } = useAuthContext();

  useAuthRedirects({ skip: isReAuthenticating });

  return isAuthenticated || isReAuthenticating ? <>{children}</> : null;
};

export default AuthRequired;

/**
 * HOC that requires a user to be signed in before rendering the passed
 * in <Component />. If a user is not signed in it redirects to /signup.
 */
export const withAuthRequired = (Component: React.ComponentType) => (props: any) => {
  return (
    <AuthRequired>
      <Component {...props} />
    </AuthRequired>
  );
};

export const useAuthRequiredInModal = ({
  skip = false,
  trigger = ModalAuthTrigger.Modal,
  onModalClose,
}: { skip?: boolean; trigger?: ModalAuthTrigger; onModalClose?: () => void } = {}) => {
  const {
    isAuthenticated,
    loading: isLoadingAuth,
    modalAuthIsOpen,
    openSignInModal,
  } = useAuthContext();

  // only display it once so a user can get out if they decide not to auth
  const didDisplay = useRef(false);

  // Mimic useEvent for callback passed as props
  // Refer to: https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md
  const onModalCloseRef = useRef(onModalClose);
  onModalCloseRef.current = onModalClose;

  // Use useLayoutEffect to ensure the modal is displayed when the layout is rendered,
  // otherwise user may experience visual issues.
  useLayoutEffect(() => {
    if (!isAuthenticated && !isLoadingAuth && !modalAuthIsOpen && !didDisplay.current && !skip) {
      didDisplay.current = true;
      openSignInModal({ trigger, onClose: onModalCloseRef.current });
    }
  }, [isAuthenticated, isLoadingAuth, modalAuthIsOpen, openSignInModal, trigger, skip]);
};
